import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { toast } from "sonner";
import AppIcon from "@/components/AppIcon";
import UrgencyBadge from "@/components/UrgencyBadge";
import WishlistToggle from "@/components/WishlistToggle";
import { ShareButton } from "@/components/ShareSheet";

const KINDS = [
  { id: "all",       label: "All"       },
  { id: "needs",     label: "Needs"     },
  { id: "offers",    label: "Offers"    },
  { id: "auctions",  label: "Auctions"  },
  { id: "leads",     label: "Services"  },
];

const KIND_BADGE = {
  need:    { bg: "bg-red-500",     label: "🔴 NEED"    },
  offer:   { bg: "bg-emerald-600", label: "🟢 OFFER"   },
  auction: { bg: "bg-blue-600",    label: "🔵 AUCTION" },
  lead:    { bg: "bg-amber-500",   label: "🟠 LEAD"    },
  matched: { bg: "bg-purple-600",  label: "🟣 MATCHED" },
  rate:    { bg: "bg-stone-600",   label: "⚪ RATE"    },
};

/**
 * P6.4 · 20 km Live discovery.
 * Delegates to /api/social/nearby which reuses the existing Live World
 * feed with `radius_km=20` hard-enforced server-side. Personal Needs
 * remain jittered ~500 m by the existing helper — this page NEVER
 * lowers that protection.
 */
export default function NearbyPage() {
  const nav = useNavigate();
  const { loc: appLoc, detect } = useUserLocation();
  // The app's resolved location (GPS / chosen city with coords); the context's Mumbai placeholder is not a location.
  const loc = appLoc && appLoc.source !== "default" && typeof appLoc.lat === "number" ? appLoc : null;
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("all");
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    if (!loc) { setBusy(false); return; }
    setBusy(true);
    try {
      const r = await api.get("/social/nearby", {
        params: { lat: loc.lat, lng: loc.lng, kind, limit: 60 },
      });
      const payload = r.data;
      setRows(payload.rows || payload || []);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please log in."); nav("/"); return; }
      toast.error("Couldn't load nearby feed");
    } finally { setBusy(false); }
  }, [loc?.lat, loc?.lng, kind, nav]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const openRow = (row) => {
    if (row.kind === "auction") nav(`/live-world/auction/${row.id}`);
    else nav(`/live-world?focus=${row.id}`);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="nearby-page">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="nearby-back">
            <ArrowLeft size={18}/>
          </button>
          <Radio size={16} className="text-emerald-600"/>
          <div className="font-black text-stone-900">Around Me · 20 km</div>
          <div className="ml-auto text-[10px] text-stone-500 font-bold inline-flex items-center gap-1">
            <MapPin size={10}/> {loc ? (loc.city || "Your location") : "Location not set"}
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-3 pb-2 flex items-center gap-1 overflow-x-auto">
          {KINDS.map((k) => (
            <button key={k.id}
                    onClick={() => setKind(k.id)}
                    data-testid={`nearby-kind-${k.id}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase whitespace-nowrap ${
                      kind === k.id ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"
                    }`}>
              {k.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-3 space-y-2">
        {!loc && (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-4 text-sm text-stone-600" data-testid="nearby-no-location">
            Set your location to see what's around you.
            {detect && <button onClick={() => detect()} className="ml-2 font-bold text-emerald-700 underline" data-testid="nearby-detect">Detect my location</button>}
          </div>
        )}
        {busy && rows.length === 0 && (
          <div className="py-16 text-center text-stone-500">
            <Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Scanning within 20 km…
          </div>
        )}
        {loc && !busy && rows.length === 0 && (
          <div className="py-16 text-center text-stone-500 text-sm" data-testid="nearby-empty">
            Nothing live within 20 km right now. Try again shortly or open the full Live World feed.
          </div>
        )}
        {rows.map((r) => {
          const badge = KIND_BADGE[r.status] || KIND_BADGE[r.kind] || KIND_BADGE.rate;
          return (
            <div key={r.id}
                 className="w-full text-left bg-white border border-stone-200 rounded-2xl p-3 flex items-center gap-3"
                 onClick={() => openRow(r)}
                 data-testid={`nearby-row-${r.id}`}>
              <div className="w-12 h-12 rounded-xl bg-stone-50 grid place-items-center overflow-hidden shrink-0">
                {r.image
                  ? <img src={r.image} alt="" className="w-full h-full object-cover"/>
                  : <AppIcon name={r.category} size={40}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black text-white inline-block px-1.5 py-0.5 rounded-full mb-0.5" style={{ background: undefined }}>
                  <span className={`inline-block px-1.5 py-0.5 rounded-full text-white ${badge.bg}`}>{badge.label}</span>
                  {r.kind === "auction" && r.meta?.closes_at && (
                    <UrgencyBadge closes_at={r.meta.closes_at} compact/>
                  )}
                </div>
                <div className="text-sm font-black text-stone-900 truncate">{r.title}</div>
                <div className="text-[11px] font-bold text-stone-600 flex items-center gap-2">
                  {r.kind === "rate" && <span className="text-stone-900">{r.price != null ? `₹${Number(r.price).toLocaleString("en-IN")}${r.unit || ""} · ${r.meta?.source || ""}` : "UNAVAILABLE"}</span>}
                  {r.bid != null && <span className="text-red-600">BID ₹{Number(r.bid).toLocaleString("en-IN")}</span>}
                  {r.ask != null && <span className="text-emerald-700">ASK ₹{Number(r.ask).toLocaleString("en-IN")}</span>}
                  {r.distance_km != null && <span className="text-stone-500">📍 {r.distance_km} km</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <WishlistToggle objectType={r.kind === "auction" ? "auction" : (r.kind === "need" ? "need" : "listing")}
                                 objectId={r.id}/>
                <ShareButton objectType={r.kind === "auction" ? "auction" : (r.kind === "need" ? "need" : "listing")}
                              objectId={r.id} title={r.title}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
